import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useEffect } from "react";
import { ApiError } from "../api.ts";
import { FolderView, ListPending } from "../components/FolderView.tsx";
import { Icon } from "../components/Icon.tsx";
import { StatePanel } from "../components/StatePanel.tsx";
import ui from "../components/ui.module.css";
import { audienceQuery, groupsQuery, messageOf, nodeQuery, nodesQuery } from "../lib/queries.ts";

export const Route = createFileRoute("/folders/$id")({
  loader: ({ context: { queryClient }, params: { id } }) =>
    Promise.all([
      queryClient.ensureQueryData(nodeQuery(id)),
      queryClient.ensureInfiniteQueryData(nodesQuery(id)),
      queryClient.ensureQueryData(audienceQuery(id)),
      queryClient.ensureQueryData(groupsQuery),
    ]),
  component: FolderPage,
  pendingComponent: ListPending,
  errorComponent: FolderError,
});

function FolderPage() {
  const { id } = Route.useParams();
  const { data: node } = useSuspenseQuery(nodeQuery(id));

  useEffect(() => {
    document.title = `${node.name} · Vault`;
  }, [node.name]);

  if (node.kind !== "folder") {
    return (
      <StatePanel
        icon="files"
        tone="info"
        title="That is a file"
        detail={`${node.name} is a file, not a folder.`}
      >
        <Link to="/files/$id" params={{ id: node.id }} className={ui.button} data-variant="primary">
          Open the file
        </Link>
      </StatePanel>
    );
  }

  return <FolderView folder={node} />;
}

function FolderError({ error, reset }: { error: unknown; reset: () => void }) {
  const router = useRouter();
  if (error instanceof ApiError && (error.status === 404 || error.status === 400)) {
    return (
      <StatePanel
        icon="folder"
        tone="info"
        title="No folder here"
        detail="The folder was deleted or the address is wrong."
      >
        <Link to="/" className={ui.button}>
          All files
        </Link>
      </StatePanel>
    );
  }
  return (
    <StatePanel icon="alert" title="This folder did not load" detail={messageOf(error)}>
      <Link to="/" className={ui.button}>
        All files
      </Link>
      <button
        type="button"
        className={ui.button}
        data-variant="primary"
        onClick={() => {
          reset();
          void router.invalidate();
        }}
      >
        <Icon name="retry" />
        Try again
      </button>
    </StatePanel>
  );
}
